import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Typography, Input, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getCreditLimitRequests,
  createCreditLimitRequest,
  updateCreditLimitRequest,
  getAvailableEnterprises,
} from "../../redux/slices/CreditLimitSlice";
import CustomButton from "../../components/CustomButton/Button";
import CustomModal from "../../components/CustomModal/CustomModal";
import CreditLimitTable from "./Components/CreditLimitTable";
import RequestCreditLimitModal from "./Components/RequestCreditLimitModal";
import "./CreditLimit.scss";

const { Title } = Typography;

const CreditLimit = () => {
  const dispatch = useDispatch();
  const creditLimitState = useSelector((state) => state.creditLimit || state);
  const { requests = [], availableEnterprises = [], loading = false, submitLoading = false } =
    creditLimitState || {};

  const activeCategory = useSelector((state) => state.dealCategory?.activeCategory);
  const user = useSelector((state) => state.auth?.user);
  const currentDealCategoryId =
    activeCategory?.dealCategoryId ||
    user?.dealCategories?.[0]?.dealCategoryId ||
    "DCT1000001";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    dispatch(getCreditLimitRequests(currentDealCategoryId));
    dispatch(getAvailableEnterprises(currentDealCategoryId));
  }, [dispatch, currentDealCategoryId]);

  const handleOpenCreateModal = () => {
    setEditingRecord(null);
    dispatch(getAvailableEnterprises(currentDealCategoryId));
    setModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setEditingRecord(record);
    dispatch(getAvailableEnterprises(currentDealCategoryId));
    setModalOpen(true);
  };

  const handleRequestAgain = (record) => {
    // Open a fresh modal pre-filled with enterprise info
    setEditingRecord({
      entId: record.entId,
      enterpriseName: record.enterpriseName,
      contactName: record.contactName,
      contactEmail: record.contactEmail,
      contactPhone: record.contactPhone,
      website: record.website,
      requestedCredit: record.requestedCredit,
      requestedTenure: record.requestedTenure,
      remarks: "",
    });
    dispatch(getAvailableEnterprises(currentDealCategoryId));
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSubmitRequest = async (payload, requestId) => {
    let res;
    const submissionPayload = {
      ...payload,
      dealCategoryId: currentDealCategoryId,
    };
    if (requestId) {
      res = await dispatch(updateCreditLimitRequest({ requestId, payload: submissionPayload }));
    } else {
      res = await dispatch(createCreditLimitRequest(submissionPayload));
    }

    if (res?.meta?.requestStatus === "fulfilled") {
      message.success(
        requestId
          ? "Credit limit request updated and re-submitted successfully."
          : "Credit limit request raised successfully."
      );
      handleCloseModal();
      dispatch(getCreditLimitRequests(currentDealCategoryId));
    } else {
      const errMsg =
        res?.payload?.message ||
        res?.payload?.error ||
        res?.payload ||
        "Failed to submit credit limit request";
      message.error(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
    }
  };

  const filteredRequests = useMemo(() => {
    if (!Array.isArray(requests)) return [];
    return requests.filter(
      (r) => (r.dealCategoryId || "DCT1000001") === currentDealCategoryId
    );
  }, [requests, currentDealCategoryId]);

  const totalRequestsCount = useMemo(() => filteredRequests?.length || 0, [filteredRequests]);

  return (
    <div className="credit-limit-container">
      <Row
        align="middle"
        justify="space-between"
        className="credit-limit-header"
        gutter={[16, 16]}
      >
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Credit Limit Requests ({totalRequestsCount})
          </Title>
        </Col>
        <Col>
          <Row gutter={12} align="middle">
            <Col>
              <Input
                placeholder="Search Enterprise"
                prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260, height: 40 }}
                allowClear
              />
            </Col>
            <Col>
              <CustomButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreateModal}
                style={{ height: 40 }}
              >
                Request Credit Limit
              </CustomButton>
            </Col>
          </Row>
        </Col>
      </Row>

      <CreditLimitTable
        data={filteredRequests}
        loading={loading}
        searchText={searchText}
        onEdit={handleOpenEditModal}
        onRequestAgain={handleRequestAgain}
      />

      {modalOpen && (
        <CustomModal
          title={editingRecord?.requestId ? "Edit & Resubmit Credit Limit Request" : "Request Credit Limit"}
          open={modalOpen}
          onCancel={handleCloseModal}
          width={700}
          centered
          content={
            <RequestCreditLimitModal
              initialData={editingRecord}
              availableEnterprises={availableEnterprises}
              onSubmit={handleSubmitRequest}
              onCancel={handleCloseModal}
              loading={submitLoading}
            />
          }
        />
      )}
    </div>
  );
};

export default CreditLimit;
